import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialer } from '@/contexts/DialerContext';
import { useCallSessionView } from '@/hooks/useCallSessionView';
import { useIncomingCallSession } from '@/hooks/useIncomingCallSession';
import { CallPageError, useCallCommands } from '@/hooks/useCallCommands';
import { useNow } from '@/hooks/useNow';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import { readNoteDraft, useCallPageStore, writeNoteDraft, type QualifyingCall } from '@/state/callPageStore';
import { PAD, applySuggestion, padEntry } from '@/services/dialer/callPage/outcomes';
import { focusKindOf, readSingleKeysPref, resolveCallPageKey, writeSingleKeysPref } from '@/services/dialer/callPage/keyboard';
import { browserTimeZone, zonedToIso } from '@/services/dialer/callPage/zonedTime';
import type { CustomerCrmView } from '@/services/dialer/bookedMeeting';
import type { PadOutcome } from '@/api/endpoints/qualifications';
import { Button } from '@/components/ui/button';
import { SessionBar } from './SessionBar';
import { LinesColumn } from './LinesColumn';
import { ProspectPane, type HistoryRow, type ProspectRecord } from './ProspectPane';
import { QualificationPane, type Draft, type PaneError, type QualificationPaneHandle } from './QualificationPane';
import { SessionSummary } from './SessionSummary';
import { ShortcutHelp, ShortcutStrip } from './ShortcutHelp';

/**
 * The call page (W3, ADR-0047, spec §4–§6), behind the call_page flag. It
 * replaces the list during a session: session bar, lines, the prospect on
 * the line, notes and outcome. Reads through useCallSessionView, writes
 * through useCallCommands; the old modal is not used here.
 */

const emptyDraft = (note = ''): Draft => ({
  outcome: null, source: null, note, followDate: '', followTime: '', timezone: browserTimeZone(),
  meetingDone: false, showMeetingForm: false, suggested: null,
});

function useWidth(): number {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

interface Props {
  customer: CustomerCrmView | null;
  onBackToList(): void;
}

export default function CallPage({ customer, onBackToList }: Props) {
  const { t } = useTranslation(['dialer']);
  const view = useCallSessionView();
  const cmd = useCallCommands();
  // Half a second: the countdown and the timers show whole seconds.
  const now = useNow(500);
  const width = useWidth();
  const compact = width < 1280;
  const d = useDialer() as unknown as {
    prospectQueue?: ProspectRecord[];
    allCalls?: Array<HistoryRow & { prospect_phone?: string | null; prospect_name?: string | null; prospect_company?: string | null; prospect_id?: string | null }>;
  };
  const store = useCallPageStore();
  const inbound = useIncomingCallSession();
  // Outside a conversation the inbound card sits at the top of the lines;
  // during one the engine ignores the call (spec §4.4).
  const incoming = inbound.isRinging && inbound.prospect && view.phase !== 'connected'
    ? { name: inbound.prospect.name, phone: inbound.prospect.phone } : null;
  const incomingProps = { incoming, onAccept: () => { void inbound.handleAccept(); }, onReject: inbound.handleReject };
  const [ask, confirmDialog] = useConfirmDialog();
  const [confirming, setConfirming] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [singleKeys, setSingleKeys] = useState(readSingleKeysPref);
  const [reconcileState, setReconcileState] = useState<'running' | 'done' | 'failed'>('running');
  const [toSettle, setToSettle] = useState<Array<{ id: string; name: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<PaneError | null>(null);
  const [draftStatus, setDraftStatus] = useState<'idle' | 'saved' | 'failed'>('idle');
  const paneRef = useRef<QualificationPaneHandle>(null);
  // QA-W7-2: with permissions_v2, calls.qualify may be removed from the role
  // ('' = allowed; always '' while the flag is off).
  const qualifyBlocked = usePermissionGuard()('calls.qualify');
  const dialogOpen = helpOpen || confirming;
  // The strip's mode follows the focus (« Saisie » in a field).
  const [inputMode, setInputMode] = useState<'input' | 'commands'>('commands');
  useEffect(() => {
    const on = () => setInputMode(focusKindOf(document.activeElement) === 'text' ? 'input' : 'commands');
    document.addEventListener('focusin', on);
    document.addEventListener('focusout', on);
    return () => { document.removeEventListener('focusin', on); document.removeEventListener('focusout', on); };
  }, []);

  // ── Which call the notes and the pad belong to (DES-18) ─────────────────
  // On a call, the notes and the pad belong to the call on the line, even
  // when another one waits for its outcome (CALL-PILL-1); otherwise to the
  // call being qualified.
  const audibleCall: QualifyingCall | null = view.audible?.parallelCallId ? {
    parallelCallId: view.audible.parallelCallId, prospectId: view.audible.prospectId, name: view.audible.name,
    phone: view.audible.phone, company: view.audible.company, endedAt: 0,
  } : null;
  const currentCall: QualifyingCall | null = view.phase === 'connected' ? audibleCall : (view.qualifying ?? audibleCall);
  const callId = currentCall?.parallelCallId ?? null;
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(callId ? readNoteDraft(callId) : ''));
  const draftFor = useRef<string | null>(callId);
  useEffect(() => {
    if (draftFor.current === callId) return;
    draftFor.current = callId;
    setDraft(emptyDraft(callId ? readNoteDraft(callId) : ''));
    setError(null);
    setDraftStatus('idle');
  }, [callId]);
  const onDraft = useCallback((patch: Partial<Draft>) => setDraft((x) => ({ ...x, ...patch })), []);

  // Note drafts survive a reload until the qualification is saved.
  useEffect(() => {
    if (!callId) return undefined;
    const timer = setTimeout(() => {
      if (!draft.note) return;
      setDraftStatus(writeNoteDraft(callId, draft.note) ? 'saved' : 'failed');
    }, 600);
    return () => clearTimeout(timer);
  }, [callId, draft.note]);

  // A machine on the audible line suggests « Messagerie » (DES-5 / ENG-6).
  const audibleLine = view.lines.find((l) => l.parallelCallId === callId);
  useEffect(() => {
    if (audibleLine?.amd !== 'machine') return;
    setDraft((x) => {
      const next = applySuggestion({ outcome: x.outcome, source: x.source }, 'left_voicemail');
      return next.source === 'ai' ? { ...x, ...next, suggested: next.outcome } : x;
    });
  }, [audibleLine?.amd]);

  // Focus: the notes at pick-up, the pad after the hang-up (spec §4.2). Keyed
  // on the call too: an answered inbound call gets its id a moment later,
  // and the fields stay disabled until then.
  const phase = view.phase;
  const focusKey = `${phase}|${callId ?? ''}`;
  const lastFocusKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastFocusKey.current === focusKey || !callId) return;
    lastFocusKey.current = focusKey;
    if (phase === 'connected') setTimeout(() => paneRef.current?.focusNote(), 0);
    if (phase === 'qualifying') setTimeout(() => paneRef.current?.focusPad(), 0);
  }, [focusKey, phase, callId]);

  // ── Reconciliation before any dial (ENG-3, spec §4.3 CALL_STATE_UNKNOWN) ─
  const runReconcile = useCallback(async () => {
    setReconcileState('running');
    try {
      const items = await cmd.reconcile();
      setToSettle(items.filter((i) => i.reason === 'unqualified').map((i) => ({ id: i.id, name: i.prospect_name || i.prospect_phone })));
      setReconcileState('done');
      return items;
    } catch {
      setReconcileState('failed');
      return null;
    }
  }, [cmd]);
  useEffect(() => {
    void runReconcile().then((items) => {
      const st = useCallPageStore.getState();
      if (!st.autoStart) return;
      st.setAutoStart(false);
      // « Lancer » from the list starts at once when nothing waits; a stale
      // line (no activity for 10 min) does not hold dialing (ENG-3).
      // Never below 1024 px (DES-10): the page says why instead.
      if (items && items.every((i) => i.reason === 'stale') && window.innerWidth >= 1024) cmd.startSession();
    });
    const online = () => { void runReconcile(); };
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dialing waits for the reconciliation, and does not start when it failed.
  const blocked: 'reconciling' | 'reconcileFailed' | 'narrow' | null = width < 1024 ? 'narrow'
    : reconcileState === 'running' ? 'reconciling' : reconcileState === 'failed' ? 'reconcileFailed' : null;

  // ── Commands ──────────────────────────────────────────────────────────────
  const hangup = useCallback(async (thenPad: boolean) => {
    await cmd.hangup(currentCall, !!view.audible?.inbound);
    if (thenPad) setTimeout(() => paneRef.current?.focusPad(), 0);
  }, [cmd, currentCall, view.audible?.inbound]);

  const select = useCallback((outcome: PadOutcome) => {
    setDraft((x) => ({ ...x, outcome, source: 'human', showMeetingForm: outcome === 'meeting_booked' && phase === 'qualifying' }));
  }, [phase]);

  const save = useCallback(async (stayPaused: boolean, meetingJustSaved = false) => {
    const call = view.qualifying;
    if (!call || !draft.outcome || saving) return;
    const needs = padEntry(draft.outcome).needs;
    let followUpAt: string | undefined;
    if (needs === 'callback') {
      followUpAt = zonedToIso(draft.followDate, draft.followTime, draft.timezone) ?? undefined;
      if (!followUpAt) { setError({ code: 'QUALIFICATION_CALLBACK_DATE_REQUIRED', correlationId: '—' }); return; }
    }
    // DES-4: the countdown starts only once the meeting itself is saved.
    if (needs === 'meeting' && !draft.meetingDone && !meetingJustSaved) { onDraft({ showMeetingForm: true }); return; }
    if (needs === 'confirm') {
      setConfirming(true);
      const ok = await ask({
        title: t('dialer:callPage.dnc.title'), description: t('dialer:callPage.dnc.description'), confirmLabel: t('dialer:callPage.dnc.confirm'),
      });
      setConfirming(false);
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await cmd.saveQualification({
        parallelCallId: call.parallelCallId, outcome: draft.outcome, note: draft.note, followUpAt,
        timezone: followUpAt ? draft.timezone : undefined, stayPaused, call, suggestedOutcome: draft.suggested,
      });
      if (result === 'unknown') {
        setError({ code: 'QUALIF_UNKNOWN', correlationId: '—' });
        void runReconcile();
      }
    } catch (err) {
      setError(err instanceof CallPageError ? { code: err.code, correlationId: err.correlationId } : { code: 'QUALIF_SAVE_FAILED', correlationId: '—' });
    } finally {
      setSaving(false);
    }
  }, [view.qualifying, draft, saving, onDraft, ask, t, cmd, runReconcile]);

  const toggleSession = useCallback(() => {
    if (phase === 'prep') { if (!blocked) cmd.startSession(); return; }
    if (phase === 'paused') { if (!blocked) cmd.resumeSession(); return; }
    if (phase === 'dialing' || phase === 'connected') cmd.pauseSession();
  }, [phase, blocked, cmd]);

  // ── The 3 s countdown; an open dialog holds it once (DES-4) ──────────────
  const countdown = view.countdown;
  const countdownDue = !!countdown && countdown.remainingMs <= 0;
  const countdownHeld = !!countdown?.held;
  useEffect(() => {
    if (!countdown) return;
    // A countdown that resumes nothing only carries « Annuler »: it ends on time.
    if (!countdown.resumes) { if (countdownDue) cmd.next(); return; }
    if (dialogOpen) { if (!countdownHeld) cmd.holdCountdown(); return; }
    if (!countdownHeld && countdownDue) cmd.next();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown?.qualificationId, countdownDue, countdownHeld, dialogOpen, cmd]);

  // ── Keyboard (spec §4.6, DES-3) ──────────────────────────────────────────
  // One listener for the page's life; it reads the latest state through a ref.
  const onKeyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  onKeyRef.current = (e: KeyboardEvent) => {
      const focus = focusKindOf(e.target);
      const action = resolveCallPageKey(e, {
        phase, focus, countdown: !!countdown?.resumes, canUndo: !!countdown?.canUndo, dialogOpen, keypadOpen: view.keypadOpen, singleKeys,
      });
      if (!action) return;
      e.preventDefault();
      // The layout's own shortcuts (« ? » help) must not also fire.
      e.stopPropagation();
      switch (action.type) {
        case 'session-toggle': toggleSession(); break;
        case 'save-note': if (callId) setDraftStatus(writeNoteDraft(callId, draft.note) ? 'saved' : 'failed'); break;
        case 'hangup': void hangup(false); break;
        case 'hangup-qualify': void hangup(true); break;
        case 'mute': cmd.toggleMute(); break;
        case 'keypad': cmd.toggleKeypad(); break;
        case 'focus-note': paneRef.current?.focusNote(); break;
        case 'outcome': { const entry = PAD[action.index]; if (entry) select(entry.outcome); break; }
        case 'save': if (!qualifyBlocked) void save(false); break;
        case 'extend': cmd.extendCountdown(); break;
        case 'undo': void cmd.undoQualification(); break;
        case 'cancel-countdown': cmd.holdCountdown(); break;
        case 'blur': (e.target as HTMLElement | null)?.blur?.(); break;
        case 'help': setHelpOpen(true); break;
      }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => onKeyRef.current(e);
    // Capture on document: runs before the layout's window listeners, whatever
    // the order the effects registered in, so stopPropagation holds.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  // ── What the centre shows ────────────────────────────────────────────────
  const consulted = useMemo(() => {
    if (!store.consultedPhone) return null;
    return (d.prospectQueue ?? []).find((p) => p.prospect_phone === store.consultedPhone) ?? null;
  }, [store.consultedPhone, d.prospectQueue]);
  const shownPhone = consulted?.prospect_phone ?? currentCall?.phone ?? '';
  const prospect = useMemo(() => consulted ?? (d.prospectQueue ?? []).find((p) => (currentCall?.prospectId && p.id === currentCall.prospectId) || (shownPhone && p.prospect_phone === shownPhone)) ?? null,
    [consulted, d.prospectQueue, currentCall?.prospectId, shownPhone]);
  const history = useMemo(() => (d.allCalls ?? [])
    .filter((c) => shownPhone && c.prospect_phone === shownPhone && c.id !== callId)
    .sort((a, b) => Date.parse(b.created_date ?? '') - Date.parse(a.created_date ?? ''))
    .slice(0, 20), [d.allCalls, shownPhone, callId]);

  const paneMode = consulted ? 'consulted' : phase === 'connected' ? 'live' : phase === 'qualifying' ? 'qualifying' : phase === 'dialing' ? 'dialing' : phase === 'paused' ? 'paused' : 'idle';

  const qualifyNow = useCallback((c: { id: string; name: string }) => {
    const row = (d.allCalls ?? []).find((x) => x.id === c.id);
    cmd.qualifyLater({
      parallelCallId: c.id, prospectId: row?.prospect_id ?? '', name: row?.prospect_name || c.name,
      phone: row?.prospect_phone ?? '', company: row?.prospect_company ?? '', endedAt: Date.now(),
    });
    setToSettle((xs) => xs.filter((x) => x.id !== c.id));
  }, [d.allCalls, cmd]);

  if (phase === 'finished') {
    return (
      <div className="flex h-full flex-col" style={{ background: 'var(--vf-bg)' }}>
        <SessionSummary view={view} onQualifyNow={qualifyNow} onCorrect={qualifyNow} onRestart={() => cmd.startSession()} onBackToList={onBackToList} />
      </div>
    );
  }


  return (
    <div className="flex h-full min-h-0 flex-col" style={{ background: 'var(--vf-bg)' }} data-testid="call-page">
      <SessionBar
        view={view}
        now={now}
        blocked={blocked}
        onStart={() => cmd.startSession()}
        onPause={() => cmd.pauseSession()}
        onResume={() => cmd.resumeSession()}
        onStop={() => { void cmd.stopSession(!!view.audible); }}
        onHelp={() => setHelpOpen(true)}
        onRetryReconcile={() => { void runReconcile(); }}
      />
      {/* Calls to settle: only between calls (prep, pause), never over a live one. */}
      {toSettle.length > 0 && (phase === 'prep' || phase === 'paused') && (
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-yellow-bg)', color: 'var(--vf-text-1)' }}>
          {t('dialer:callPage.summary.unqualified', { count: toSettle.length })}
          {toSettle.slice(0, 3).map((c) => (
            <Button key={c.id} size="sm" variant="outline" className="min-h-11" onClick={() => qualifyNow(c)}>
              {t('dialer:callPage.summary.qualifyNow')} · {c.name}
            </Button>
          ))}
        </div>
      )}
      {compact && (
        <LinesColumn lines={view.lines} audibleId={view.audible?.parallelCallId ?? null} missedInbound={view.missedInbound} now={now} compact {...incomingProps}
          onConsult={(l) => store.setConsulted(l.parallelCallId === view.audible?.parallelCallId ? null : l.phone)} />
      )}
      <div className="flex min-h-0 flex-1">
        {!compact && (
          <LinesColumn lines={view.lines} audibleId={view.audible?.parallelCallId ?? null} missedInbound={view.missedInbound} now={now} compact={false} {...incomingProps}
            onConsult={(l) => store.setConsulted(l.parallelCallId === view.audible?.parallelCallId ? null : l.phone)} />
        )}
        <ProspectPane
          mode={paneMode}
          name={consulted?.prospect_name ?? currentCall?.name ?? ''}
          phone={shownPhone}
          company={consulted?.prospect_company ?? currentCall?.company ?? ''}
          startedAt={view.audible?.startedAt ?? null}
          inbound={!!view.audible?.inbound}
          prospect={prospect}
          history={history}
          now={now}
          isMuted={view.isMuted}
          keypadOpen={view.keypadOpen}
          onMute={cmd.toggleMute}
          onKeypad={cmd.toggleKeypad}
          onHangup={() => { void hangup(false); }}
          onHangupQualify={() => { void hangup(true); }}
          onBackToCall={consulted ? () => store.setConsulted(null) : undefined}
        />
        <QualificationPane
          ref={paneRef}
          view={view}
          callName={currentCall?.name ?? ''}
          prospectId={currentCall?.prospectId || prospect?.id || null}
          parallelCallId={callId}
          customer={customer}
          draft={draft}
          draftStatus={draftStatus}
          saving={saving}
          error={error}
          enabled={!!currentCall}
          onDraft={onDraft}
          onSelect={select}
          onSave={(stay) => { void save(stay); }}
          saveBlockedReason={qualifyBlocked}
          onMeetingDone={() => { onDraft({ meetingDone: true, showMeetingForm: false }); void save(false, true); }}
          onUndo={() => { void cmd.undoQualification(); }}
          onHold={cmd.holdCountdown}
          onNext={cmd.next}
        />
      </div>
      <ShortcutStrip mode={inputMode} />
      <ShortcutHelp open={helpOpen} onOpenChange={setHelpOpen} singleKeys={singleKeys} onSingleKeys={(on) => { setSingleKeys(on); writeSingleKeysPref(on); }} />
      {confirmDialog}
    </div>
  );
}
